import {
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';

import { RTFOCommandCenterTimeAnalysisFracChart } from './RTFOCommandCenterTimeAnalysisFracChart';
import { useMemo, useState } from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { RTFOCommandCenterTonnesPerDayChart } from './RTFOCommandCenterTonnesPerDayChart';
import { RTFOCommandCenterStageProgressChart } from './RTFOCommandCenterStageProgressChart';

const RTFOCommandCenter = ({ currentRTFO, theme, darkMode }) => {
  const [filterWellNames] = useState(['All']);
  const [filterBenchNames] = useState(['All']);

  const rows = useMemo(() => {
    if (!currentRTFO?.stages?.length > 0) return [];
    let stages = [];

    currentRTFO?.stages?.forEach(stageName => {
      const stage = currentRTFO?.stageData?.[stageName];

      if (stage) {
        stages.push({
          id: stageName,
          stageName,
          stageNumber: stageName?.split('_')?.[2],
          wellName: stage?.wellName,
          wellUwi: stage?.wellUwi,
          fracScore: `${(Math.random() * 50 + 50)?.toFixed(1)}%`,
          isolationScore: `${(Math.random() * 50 + 50)?.toFixed(1)}%`
        });
      }
    });

    return stages;
  }, [currentRTFO]);

  const billingSummaryRows = useMemo(() => {
    if (!currentRTFO?.padSummary?.rateAtMaxProppant) return [];
    let stages = [];

    const wells = Object.keys(currentRTFO?.padSummary?.rateAtMaxProppant)?.sort();

    for (const wellName of wells) {

      const stageNames = Object.keys(currentRTFO?.padSummary?.rateAtMaxProppant?.[wellName]?.perStage)?.sort((a,b) => +a.split('_')?.[2] - +b.split('_')?.[2]);

      for (const stageName of stageNames) {
        const stage = currentRTFO?.stageData?.[stageName];
        stages.push({
          id: stageName,
          stageName,
          stageNumber: stageName?.split('_')?.[2],
          wellName: stage?.wellName,
          wellUwi: stage?.wellUwi,
          averageRate: +((currentRTFO?.padSummary?.rateAtMaxProppant?.[wellName]?.perStage?.[stageName]?.averageRate || 0)?.toFixed(3)),
          totalDurationHours: +((currentRTFO?.padSummary?.rateAtMaxProppant?.[wellName]?.perStage?.[stageName]?.totalDurationHours || 0)?.toFixed(3))
        });
      };
    };

    return stages;
  }, [currentRTFO]);

  const columns = [
    {
      field: 'stageName',
      headerName: 'Stage Name',
      width: 175,
      editable: false,
      sortable: true,
      filterable: true,
      type: 'string'
    },
    {
      field: 'wellName',
      headerName: 'Well Name',
      width: 100,
      editable: false,
      sortable: true,
      filterable: true,
      type: 'string'
    },
    {
      field: 'wellUwi',
      headerName: 'Well UWI',
      width: 150,
      editable: false,
      sortable: true,
      filterable: true,
      type: 'string'
    },
    {
      field: 'stageNumber',
      headerName: 'Stage Number',
      width: 100,
      editable: false,
      sortable: true,
      filterable: true,
      type: 'string'
    },
    {
      field: 'fracScore',
      headerName: 'Frac Score',
      width: 100,
      editable: false,
      sortable: true,
      filterable: true,
      type: 'number'
    },
    {
      field: 'isolationScore',
      headerName: 'Isolation Score',
      width: 100,
      editable: false,
      sortable: true,
      filterable: true,
      type: 'number'
    }
  ];

  const billingSummaryColumns = [
    {
      field: 'stageName',
      headerName: 'Stage Name',
      width: 175,
      editable: false,
      sortable: true,
      filterable: true,
      type: 'string'
    },
    {
      field: 'wellName',
      headerName: 'Well Name',
      width: 100,
      editable: false,
      sortable: true,
      filterable: true,
      type: 'string'
    },
    {
      field: 'wellUwi',
      headerName: 'Well UWI',
      width: 150,
      editable: false,
      sortable: true,
      filterable: true,
      type: 'string'
    },
    {
      field: 'stageNumber',
      headerName: 'Stage Number',
      width: 100,
      editable: false,
      sortable: true,
      filterable: true,
      type: 'string'
    },
    {
      field: 'averageRate',
      headerName: 'Avg. Rate at Max Prop. (m3/min)',
      width: 200,
      editable: false,
      sortable: true,
      filterable: true,
      type: 'number'
    },
    {
      field: 'totalDurationHours',
      headerName: 'Total Duration at Max Prop. (hrs)',
      width: 200,
      editable: false,
      sortable: true,
      filterable: true,
      type: 'number'
    }
  ];

  return (
    <Paper
      elevation={0}
      sx={{ boxShadow: 1 }}
      style={{
        padding: '20px',
        margin: '10px 20px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        backgroundColor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light']
      }}
    >
      <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between'}}>
        <Typography variant="h5">
          Command Center
        </Typography>

        <div style={{display: 'flex', flexDirection: 'row' }}>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <Typography fontSize="12px" style={{ marginRight: 10 }}>
              Well:
            </Typography>
            <FormControl>
              <Select
                value={filterWellNames}
                size="small"
                multiple
                renderValue={selected => selected.join(', ')}
                style={{
                  width: 150,
                  height: 28,
                  backgroundColor: !darkMode && '#fff',
                  fontSize: '12px'
                }}
              >
                <MenuItem
                  value={'All'}
                  // onClick={() => handlePadCheckClick('selectAll')}
                  style={{ height: 30 }}
                >
                  <Checkbox checked={filterWellNames?.includes('All')} />
                  <ListItemText primary={'All'} />
                </MenuItem>
                {currentRTFO?.perfSchedule?.wells &&
                  Object.keys(currentRTFO?.perfSchedule?.wells)?.map(wellName => {
                    return (
                      <MenuItem
                        key={wellName}
                        value={wellName}
                        // onClick={() => handlePadCheckClick('selectAll')}
                        style={{ height: 30 }}
                      >
                        <Checkbox checked={filterWellNames?.includes(wellName)} />
                        <ListItemText primary={wellName} />
                      </MenuItem>
                    );
                  })}
              </Select>
            </FormControl>
          </div>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginLeft: 40 }}>
            <Typography fontSize="12px" style={{ marginRight: 10 }}>
              Bench:
            </Typography>
            <FormControl>
              <Select
                value={filterBenchNames}
                size="small"
                multiple
                renderValue={selected => selected.join(', ')}
                style={{
                  width: 150,
                  height: 28,
                  backgroundColor: !darkMode && '#fff',
                  fontSize: '12px'
                }}
              >
                <MenuItem
                  value={'All'}
                  // onClick={() => handlePadCheckClick('selectAll')}
                  style={{ height: 30 }}
                >
                  <Checkbox checked={filterBenchNames?.includes('All')} />
                  <ListItemText primary={'All'} />
                </MenuItem>
              </Select>
            </FormControl>
          </div>
        </div>
      </div>

      {/* <div style={{ height: 600 }}>
        <RTFOContinuousPressureChart currentRTFO={currentRTFO} theme={theme} darkMode={darkMode} isLoadingPressureData={false} />
      </div>

      <Divider /> */}

      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', width: '100%' }}>
        <Typography variant="h6" style={{ marginTop: 20 }}>
          Pad Summary
        </Typography>
        
        <div style={{display: 'flex', flexDirection: 'row', width: '100%'}}>

          <div style={{ width: 300, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', marginBottom: 40 }}>
            <div style={{ marginBottom: 50, width: 150 }}>
              <Typography variant="body2">Pad Frac Score</Typography>
              <div style={{ position: 'relative', marginTop: 10, width: 100, height: 100 }}>
                <CircularProgress
                  variant="determinate"
                  value={75}
                  style={{ height: 100, width: 100, margin: 0, padding: 0 }}
                  thickness={5}
                  size="large"
                />
                <Typography
                  variant="h4"
                  style={{
                    position: 'absolute',
                    textAlign: 'center',
                    width: 60,
                    height: 60,
                    top: 30,
                    left: 20,
                    // transform: 'translate(-50%)',
                  }}
                >
                  75
                </Typography>
              </div>
            </div>

            <div style={{ width: 150 }}>
              <Typography variant="body2">Pad Isolation Score</Typography>
              <div style={{ position: 'relative', marginTop: 10, width: 100, height: 100 }}>
                <CircularProgress
                  variant="determinate"
                  value={75}
                  style={{ height: 100, width: 100, margin: 0, padding: 0 }}
                  thickness={5}
                  size="large"
                />
                <Typography
                  variant="h4"
                  style={{
                    position: 'absolute',
                    textAlign: 'center',
                    width: 60,
                    height: 60,
                    top: 30,
                    left: 20,
                    // transform: 'translate(-50%)',
                  }}
                >
                  75
                </Typography>
              </div>
            </div>
          </div>

          <div style={{width: 'calc(100% - 300px)', height: '100%'}}>
            <RTFOCommandCenterStageProgressChart />

          </div>
        </div>
      </div>

      {/* GENERAL INFORMATION AND PRODUCT USED */}
      <div style={{ width: '100%', display: 'flex', flexDirection: 'row', marginTop: 20, marginBottom: 20 }}>
        <div style={{ width: '50%', padding: 10 }}>
          <Typography variant="h6">General Information</Typography>

          <TableContainer>
            <Table size="small">
              {/* <TableHead>
                <TableRow>
                  <TableCell>Parameter</TableCell>
                  <TableCell align="right">Value</TableCell>
                </TableRow>
              </TableHead> */}
              <TableBody>
                <TableRow
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:nth-of-type(odd)': {
                      backgroundColor: darkMode ? '#263238' : 'whitesmoke'
                    }
                  }}
                >
                  <TableCell component="th" scope="row">
                    Number of wells
                  </TableCell>
                  <TableCell align="right">
                    {currentRTFO?.perfSchedule?.wells ? Object.keys(currentRTFO?.perfSchedule?.wells)?.length : 0}
                  </TableCell>
                </TableRow>

                <TableRow
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:nth-of-type(odd)': {
                      backgroundColor: darkMode ? '#263238' : 'whitesmoke'
                    }
                  }}
                >
                  <TableCell component="th" scope="row">
                    Number of stages
                  </TableCell>
                  <TableCell align="right">{getParamValue('Number of stages')}</TableCell>
                </TableRow>

                {currentRTFO?.perfSchedule?.wells &&
                  Object.keys(currentRTFO?.perfSchedule?.wells).map(wellName => {
                    return (
                      <TableRow
                        key={wellName}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          '&:nth-of-type(odd)': {
                            backgroundColor: darkMode ? '#263238' : 'whitesmoke'
                          }
                        }}
                      >
                        <TableCell component="th" scope="row" style={{ paddingLeft: 40 }}>
                          {wellName?.charAt(0)?.toUpperCase()?.concat(wellName?.substring(1))}
                        </TableCell>
                        <TableCell align="right">{getParamValue('Number of stages', wellName)}</TableCell>
                      </TableRow>
                    );
                  })}

                <TableRow
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:nth-of-type(odd)': {
                      backgroundColor: darkMode ? '#263238' : 'whitesmoke'
                    }
                  }}
                >
                  <TableCell component="th" scope="row">
                    Number of clusters per stage
                  </TableCell>
                  <TableCell align="right">{getParamValue('Number of clusters per stage')}</TableCell>
                </TableRow>

                {currentRTFO?.perfSchedule?.wells &&
                  Object.keys(currentRTFO?.perfSchedule?.wells).map(wellName => {
                    return (
                      <TableRow
                        key={wellName}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          '&:nth-of-type(odd)': {
                            backgroundColor: darkMode ? '#263238' : 'whitesmoke'
                          }
                        }}
                      >
                        <TableCell component="th" scope="row" style={{ paddingLeft: 40 }}>
                          {wellName?.charAt(0)?.toUpperCase()?.concat(wellName?.substring(1))}
                        </TableCell>
                        <TableCell align="right">{getParamValue('Number of clusters per stage', wellName)}</TableCell>
                      </TableRow>
                    );
                  })}

                <TableRow
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:nth-of-type(odd)': {
                      backgroundColor: darkMode ? '#263238' : 'whitesmoke'
                    }
                  }}
                >
                  <TableCell component="th" scope="row">
                    Number of shots per cluster
                  </TableCell>
                  <TableCell align="right">{getParamValue('Number of shots per cluster')}</TableCell>
                </TableRow>

                {currentRTFO?.perfSchedule?.wells &&
                  Object.keys(currentRTFO?.perfSchedule?.wells).map(wellName => {
                    return (
                      <TableRow
                        key={wellName}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          '&:nth-of-type(odd)': {
                            backgroundColor: darkMode ? '#263238' : 'whitesmoke'
                          }
                        }}
                      >
                        <TableCell component="th" scope="row" style={{ paddingLeft: 40 }}>
                          {wellName?.charAt(0)?.toUpperCase()?.concat(wellName?.substring(1))}
                        </TableCell>
                        <TableCell align="right">{getParamValue('Number of shots per cluster', wellName)}</TableCell>
                      </TableRow>
                    );
                  })}

                <TableRow
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:nth-of-type(odd)': {
                      backgroundColor: darkMode ? '#263238' : 'whitesmoke'
                    }
                  }}
                >
                  <TableCell component="th" scope="row">
                    Entry hole size
                  </TableCell>
                  <TableCell align="right">{getParamValue('Entry hole size')}</TableCell>
                </TableRow>

                {currentRTFO?.perfSchedule?.wells &&
                  Object.keys(currentRTFO?.perfSchedule?.wells).map(wellName => {
                    return (
                      <TableRow
                        key={wellName}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          '&:nth-of-type(odd)': {
                            backgroundColor: darkMode ? '#263238' : 'whitesmoke'
                          }
                        }}
                      >
                        <TableCell component="th" scope="row" style={{ paddingLeft: 40 }}>
                          {wellName?.charAt(0)?.toUpperCase()?.concat(wellName?.substring(1))}
                        </TableCell>
                        <TableCell align="right">{getParamValue('Entry hole size', wellName)}</TableCell>
                      </TableRow>
                    );
                  })}

                <TableRow
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:nth-of-type(odd)': {
                      backgroundColor: darkMode ? '#263238' : 'whitesmoke'
                    }
                  }}
                >
                  <TableCell component="th" scope="row">
                    Number of pumpdowns
                  </TableCell>
                  <TableCell align="right">{getParamValue('Number of pumpdowns')}</TableCell>
                </TableRow>

                {currentRTFO?.perfSchedule?.wells &&
                  Object.keys(currentRTFO?.perfSchedule?.wells).map(wellName => {
                    return (
                      <TableRow
                        key={wellName}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          '&:nth-of-type(odd)': {
                            backgroundColor: darkMode ? '#263238' : 'whitesmoke'
                          }
                        }}
                      >
                        <TableCell component="th" scope="row" style={{ paddingLeft: 40 }}>
                          {wellName?.charAt(0)?.toUpperCase()?.concat(wellName?.substring(1))}
                        </TableCell>
                        <TableCell align="right">{getParamValue('Number of pumpdowns', wellName)}</TableCell>
                      </TableRow>
                    );
                  })}

                <TableRow
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:nth-of-type(odd)': {
                      backgroundColor: darkMode ? '#263238' : 'whitesmoke'
                    }
                  }}
                >
                  <TableCell component="th" scope="row">
                    Completed length
                  </TableCell>
                  <TableCell align="right">{getParamValue('Completed length')}</TableCell>
                </TableRow>

                {currentRTFO?.perfSchedule?.wells &&
                  Object.keys(currentRTFO?.perfSchedule?.wells).map(wellName => {
                    return (
                      <TableRow
                        key={wellName}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          '&:nth-of-type(odd)': {
                            backgroundColor: darkMode ? '#263238' : 'whitesmoke'
                          }
                        }}
                      >
                        <TableCell component="th" scope="row" style={{ paddingLeft: 40 }}>
                          {wellName?.charAt(0)?.toUpperCase()?.concat(wellName?.substring(1))}
                        </TableCell>
                        <TableCell align="right">{getParamValue('Completed length', wellName)}</TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="h6" style={{ marginTop: 20 }}>
            Casing Information
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Casing</TableCell>
                  <TableCell align="right">ID (mm)</TableCell>
                  <TableCell align="right">OD (mm)</TableCell>
                  <TableCell align="right">MD (mMD)</TableCell>
                  <TableCell align="right">fc</TableCell>
                  <TableCell align="right">plExp</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentRTFO?.perfSchedule?.wells &&
                  Object.keys(currentRTFO?.perfSchedule?.wells)?.map(wellName => {
                    return ['casing1', 'casing2'].map(casingName => {
                      return (
                        <TableRow
                          key={`${wellName}-${casingName}`}
                          sx={{
                            '&:last-child td, &:last-child th': { border: 0 },
                            '&:nth-of-type(odd)': {
                              backgroundColor: darkMode ? '#263238' : 'whitesmoke'
                            }
                          }}
                        >
                          <TableCell component="th" scope="row" style={{ paddingLeft: 40 }}>
                            {`${wellName?.charAt(0)?.toUpperCase()?.concat(wellName?.substring(1))} - ${casingName === 'casing1' ? 'Casing 1' : 'Casing 2'}`}
                          </TableCell>
                          <TableCell align="right">{getParamValue('Casing id', wellName, casingName)}</TableCell>
                          <TableCell align="right">{getParamValue('Casing od', wellName, casingName)}</TableCell>
                          <TableCell align="right">{getParamValue('Casing md', wellName, casingName)}</TableCell>
                          <TableCell align="right">{getParamValue('Casing fc', wellName, casingName)}</TableCell>
                          <TableCell align="right">{getParamValue('Casing plExp', wellName, casingName)}</TableCell>
                        </TableRow>
                      );
                    });
                  })}
                {}
              </TableBody>
            </Table>
          </TableContainer>
        </div>

        <div style={{ width: '50%', padding: 10 }}>
          <Typography variant="h6">Product Used</Typography>

          <Table size="small">
            {/* <TableHead>
              <TableRow>
                <TableCell>Parameter</TableCell>
                <TableCell align="right">Value</TableCell>
              </TableRow>
            </TableHead> */}
            <TableBody>
              <TableRow
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  '&:nth-of-type(odd)': {
                    backgroundColor: darkMode ? '#263238' : 'whitesmoke'
                  }
                }}
              >
                <TableCell component="th" scope="row">
                  Total water used for frac
                </TableCell>
                <TableCell align="right">{getParamValue('Total water used for frac')}</TableCell>
              </TableRow>

              {currentRTFO?.perfSchedule?.wells &&
                Object.keys(currentRTFO?.perfSchedule?.wells).map(wellName => {
                  return (
                    <TableRow
                      key={wellName}
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        '&:nth-of-type(odd)': {
                          backgroundColor: darkMode ? '#263238' : 'whitesmoke'
                        }
                      }}
                    >
                      <TableCell component="th" scope="row" style={{ paddingLeft: 40 }}>
                        {wellName?.charAt(0)?.toUpperCase()?.concat(wellName?.substring(1))}
                      </TableCell>
                      <TableCell align="right">{getParamValue('Total water used for frac', wellName)}</TableCell>
                    </TableRow>
                  );
                })}

              <TableRow
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  '&:nth-of-type(odd)': {
                    backgroundColor: darkMode ? '#263238' : 'whitesmoke'
                  }
                }}
              >
                <TableCell component="th" scope="row">
                  Total water used for pumpdown
                </TableCell>
                <TableCell align="right">{getParamValue('Total water used for pumpdown')}</TableCell>
              </TableRow>

              {currentRTFO?.perfSchedule?.wells &&
                Object.keys(currentRTFO?.perfSchedule?.wells).map(wellName => {
                  return (
                    <TableRow
                      key={wellName}
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        '&:nth-of-type(odd)': {
                          backgroundColor: darkMode ? '#263238' : 'whitesmoke'
                        }
                      }}
                    >
                      <TableCell component="th" scope="row" style={{ paddingLeft: 40 }}>
                        {wellName?.charAt(0)?.toUpperCase()?.concat(wellName?.substring(1))}
                      </TableCell>
                      <TableCell align="right">{getParamValue('Total water used for pumpdown', wellName)}</TableCell>
                    </TableRow>
                  );
                })}

              <TableRow
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  '&:nth-of-type(odd)': {
                    backgroundColor: darkMode ? '#263238' : 'whitesmoke'
                  }
                }}
              >
                <TableCell component="th" scope="row">
                  Total proppant
                </TableCell>
                <TableCell align="right">{getParamValue('Total proppant')}</TableCell>
              </TableRow>

              {currentRTFO?.perfSchedule?.wells &&
                Object.keys(currentRTFO?.perfSchedule?.wells).map(wellName => {
                  return (
                    <TableRow
                      key={wellName}
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        '&:nth-of-type(odd)': {
                          backgroundColor: darkMode ? '#263238' : 'whitesmoke'
                        }
                      }}
                    >
                      <TableCell component="th" scope="row" style={{ paddingLeft: 40 }}>
                        {wellName?.charAt(0)?.toUpperCase()?.concat(wellName?.substring(1))}
                      </TableCell>
                      <TableCell align="right">{getParamValue('Total proppant', wellName)}</TableCell>
                    </TableRow>
                  );
                })}

              <TableRow
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  '&:nth-of-type(odd)': {
                    backgroundColor: darkMode ? '#263238' : 'whitesmoke'
                  }
                }}
              >
                <TableCell component="th" scope="row">
                  Total chemical/FR
                </TableCell>
                <TableCell align="right">{getParamValue('Total chemical/FR')}</TableCell>
              </TableRow>

              {currentRTFO?.perfSchedule?.wells &&
                Object.keys(currentRTFO?.perfSchedule?.wells).map(wellName => {
                  return (
                    <TableRow
                      key={wellName}
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        '&:nth-of-type(odd)': {
                          backgroundColor: darkMode ? '#263238' : 'whitesmoke'
                        }
                      }}
                    >
                      <TableCell component="th" scope="row" style={{ paddingLeft: 40 }}>
                        {wellName?.charAt(0)?.toUpperCase()?.concat(wellName?.substring(1))}
                      </TableCell>
                      <TableCell align="right">{getParamValue('Total chemical/FR', wellName)}</TableCell>
                    </TableRow>
                  );
                })}

              <TableRow
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  '&:nth-of-type(odd)': {
                    backgroundColor: darkMode ? '#263238' : 'whitesmoke'
                  }
                }}
              >
                <TableCell component="th" scope="row">
                  Frac plugs used
                </TableCell>
                <TableCell align="right">{getParamValue('Frac plugs used')}</TableCell>
              </TableRow>

              {currentRTFO?.perfSchedule?.wells &&
                Object.keys(currentRTFO?.perfSchedule?.wells).map(wellName => {
                  return (
                    <TableRow
                      key={wellName}
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        '&:nth-of-type(odd)': {
                          backgroundColor: darkMode ? '#263238' : 'whitesmoke'
                        }
                      }}
                    >
                      <TableCell component="th" scope="row" style={{ paddingLeft: 40 }}>
                        {wellName?.charAt(0)?.toUpperCase()?.concat(wellName?.substring(1))}
                      </TableCell>
                      <TableCell align="right">{getParamValue('Frac plugs used', wellName)}</TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>

          <div style={{ width: '100%', height: 540, display: 'flex', flexDirection: 'column', marginTop: 40 }}>
            <RTFOCommandCenterTonnesPerDayChart />
          </div>
        </div>
      </div>

      <Divider />

      {/* TIME ANALYSIS */}
      <div style={{ width: '100%', padding: 10 }}>
        <Typography variant="h6">Time Analysis</Typography>

        <div style={{ width: '100%', display: 'flex', flexDirection: 'row' }}>
          <div style={{ width: '50%' }}>
            <Table size="small">
              {/* <TableHead>
              <TableRow>
                <TableCell>Parameter</TableCell>
                <TableCell align="right">Value</TableCell>
              </TableRow>
            </TableHead> */}
              <TableBody>
                {/* {['Start date', 'End date', 'Total frac time', 'Total time between fracs']?.map(param => (
              ))} */}
                <TableRow
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:nth-of-type(odd)': {
                      backgroundColor: darkMode ? '#263238' : 'whitesmoke'
                    }
                  }}
                >
                  <TableCell component="th" scope="row">
                    Start date
                  </TableCell>
                  <TableCell align="right">{getParamValue('Start date')}</TableCell>
                </TableRow>

                {currentRTFO?.perfSchedule?.wells &&
                  Object.keys(currentRTFO?.perfSchedule?.wells).map(wellName => {
                    return (
                      <TableRow
                        key={wellName}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          '&:nth-of-type(odd)': {
                            backgroundColor: darkMode ? '#263238' : 'whitesmoke'
                          }
                        }}
                      >
                        <TableCell component="th" scope="row" style={{ paddingLeft: 40 }}>
                          {wellName?.charAt(0)?.toUpperCase()?.concat(wellName?.substring(1))}
                        </TableCell>
                        <TableCell align="right">{getParamValue('Start date', wellName)}</TableCell>
                      </TableRow>
                    );
                  })}

                <TableRow
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:nth-of-type(odd)': {
                      backgroundColor: darkMode ? '#263238' : 'whitesmoke'
                    }
                  }}
                >
                  <TableCell component="th" scope="row">
                    End date
                  </TableCell>
                  <TableCell align="right">{getParamValue('End date')}</TableCell>
                </TableRow>

                {currentRTFO?.perfSchedule?.wells &&
                  Object.keys(currentRTFO?.perfSchedule?.wells).map(wellName => {
                    return (
                      <TableRow
                        key={wellName}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          '&:nth-of-type(odd)': {
                            backgroundColor: darkMode ? '#263238' : 'whitesmoke'
                          }
                        }}
                      >
                        <TableCell component="th" scope="row" style={{ paddingLeft: 40 }}>
                          {wellName?.charAt(0)?.toUpperCase()?.concat(wellName?.substring(1))}
                        </TableCell>
                        <TableCell align="right">{getParamValue('End date', wellName)}</TableCell>
                      </TableRow>
                    );
                  })}

                <TableRow
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:nth-of-type(odd)': {
                      backgroundColor: darkMode ? '#263238' : 'whitesmoke'
                    }
                  }}
                >
                  <TableCell component="th" scope="row">
                    Total frac time
                  </TableCell>
                  <TableCell align="right">{getParamValue('Total frac time')}</TableCell>
                </TableRow>

                {currentRTFO?.perfSchedule?.wells &&
                  Object.keys(currentRTFO?.perfSchedule?.wells).map(wellName => {
                    return (
                      <TableRow
                        key={wellName}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          '&:nth-of-type(odd)': {
                            backgroundColor: darkMode ? '#263238' : 'whitesmoke'
                          }
                        }}
                      >
                        <TableCell component="th" scope="row" style={{ paddingLeft: 40 }}>
                          {wellName?.charAt(0)?.toUpperCase()?.concat(wellName?.substring(1))}
                        </TableCell>
                        <TableCell align="right">{getParamValue('Total frac time', wellName)}</TableCell>
                      </TableRow>
                    );
                  })}

                <TableRow
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:nth-of-type(odd)': {
                      backgroundColor: darkMode ? '#263238' : 'whitesmoke'
                    }
                  }}
                >
                  <TableCell component="th" scope="row">
                    Total time between fracs
                  </TableCell>
                  <TableCell align="right">{getParamValue('Total time between fracs')}</TableCell>
                </TableRow>

                {/* {currentRTFO?.perfSchedule?.wells &&
                  Object.keys(currentRTFO?.perfSchedule?.wells).map(wellName => {
                    return (
                      <TableRow
                        key={wellName}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          '&:nth-of-type(odd)': {
                            backgroundColor: darkMode ? '#263238' : 'whitesmoke'
                          }
                        }}
                      >
                        <TableCell component="th" scope="row" style={{ paddingLeft: 40 }}>
                          {wellName?.charAt(0)?.toUpperCase()?.concat(wellName?.substring(1))}
                        </TableCell>
                        <TableCell align="right">{getParamValue('Total time between fracs', wellName)}</TableCell>
                      </TableRow>
                    );
                  })} */}

                <TableRow
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:nth-of-type(odd)': {
                      backgroundColor: darkMode ? '#263238' : 'whitesmoke'
                    }
                  }}
                >
                  <TableCell component="th" scope="row">
                    Average pumpdown time
                  </TableCell>
                  <TableCell align="right">{getParamValue('Average pumpdown time')}</TableCell>
                </TableRow>

                {currentRTFO?.perfSchedule?.wells &&
                  Object.keys(currentRTFO?.perfSchedule?.wells).map(wellName => {
                    return (
                      <TableRow
                        key={wellName}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          '&:nth-of-type(odd)': {
                            backgroundColor: darkMode ? '#263238' : 'whitesmoke'
                          }
                        }}
                      >
                        <TableCell component="th" scope="row" style={{ paddingLeft: 40 }}>
                          {wellName?.charAt(0)?.toUpperCase()?.concat(wellName?.substring(1))}
                        </TableCell>
                        <TableCell align="right">{getParamValue('Average pumpdown time', wellName)}</TableCell>
                      </TableRow>
                    );
                  })}

                <TableRow
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:nth-of-type(odd)': {
                      backgroundColor: darkMode ? '#263238' : 'whitesmoke'
                    }
                  }}
                >
                  <TableCell component="th" scope="row">
                    Average time on bottom
                  </TableCell>
                  <TableCell align="right">{getParamValue('Average time on bottom')}</TableCell>
                </TableRow>

                {currentRTFO?.perfSchedule?.wells &&
                  Object.keys(currentRTFO?.perfSchedule?.wells).map(wellName => {
                    return (
                      <TableRow
                        key={wellName}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          '&:nth-of-type(odd)': {
                            backgroundColor: darkMode ? '#263238' : 'whitesmoke'
                          }
                        }}
                      >
                        <TableCell component="th" scope="row" style={{ paddingLeft: 40 }}>
                          {wellName?.charAt(0)?.toUpperCase()?.concat(wellName?.substring(1))}
                        </TableCell>
                        <TableCell align="right">{getParamValue('Average time on bottom', wellName)}</TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </div>

          <div style={{ width: '50%', height: 540, display: 'flex', flexDirection: 'column' }}>
            <RTFOCommandCenterTimeAnalysisFracChart />
          </div>
        </div>
      </div>

      <Divider />

      {/* TECHNICAL ANALYSIS SUMMARY */}
      <div style={{ marginTop: 20, width: '100%', position: 'relative' }}>
        <Typography variant="h6" style={{ marginBottom: '20px' }}>
          Technical Analysis Summary
        </Typography>

        <div style={{ height: 800 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            rowsPerPageOptions={[10, 25, 50, 100]}
            disableSelectionOnClick
            components={{
              Toolbar: GridToolbar
            }}
            sx={{
              '& .MuiDataGrid-row:nth-of-type(odd)': {
                backgroundColor: darkMode ? 'grey' : 'whitesmoke'
              }
            }}
            style={{ fontSize: '12px' }}
            density="compact"
          />
        </div>
      </div>

      {/* BILLING SUMMARY */}
      <div style={{ marginTop: 20, width: '100%', position: 'relative' }}>
        <Typography variant="h6" style={{ marginBottom: '20px' }}>
          Billing Summary
        </Typography>

        <div style={{ height: 800 }}>
          <DataGrid
            rows={billingSummaryRows}
            columns={billingSummaryColumns}
            rowsPerPageOptions={[10, 25, 50, 100]}
            disableSelectionOnClick
            components={{
              Toolbar: GridToolbar
            }}
            sx={{
              '& .MuiDataGrid-row:nth-of-type(odd)': {
                backgroundColor: darkMode ? 'grey' : 'whitesmoke'
              }
            }}
            style={{ fontSize: '12px' }}
            density="compact"
          />
        </div>
      </div>
    </Paper>
  );

  function getParamValue(param, wellName, casingName) {
    switch (param) {
      case 'Number of stages': {
        if (wellName) {
          return currentRTFO?.stages?.filter(s => s.toLowerCase()?.includes(wellName?.toLowerCase()))?.length || 0;
        }
        return currentRTFO?.stages?.length || 0;
      }
      case 'Number of clusters per stage': {
        let min = 0;
        let max = 0;
        let stageNames = [];

        if (wellName) {
          stageNames = currentRTFO?.stages?.filter(s => s.toLowerCase()?.includes(wellName?.toLowerCase())) || [];

          const numClustersArray = stageNames?.map(stageName => {
            const stageNumber = stageName?.split('_')?.[2];
            const stageClusters = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[stageNumber]?.clusters;
            const clusterNames = stageClusters && Object.keys(stageClusters);
            const numClusters = clusterNames?.length || 0;
            return numClusters;
          });

          min = Math.min(...numClustersArray) || 0;
          max = Math.max(...numClustersArray) || 0;
        } else {
          const numClustersArray = Object.keys(currentRTFO?.perfSchedule?.wells)
            .map(wellName => {
              stageNames = currentRTFO?.stages?.filter(s => s.toLowerCase()?.includes(wellName?.toLowerCase())) || [];

              return stageNames?.map(stageName => {
                const stageNumber = stageName?.split('_')?.[2];
                const stageClusters = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[stageNumber]?.clusters;
                const clusterNames = stageClusters && Object.keys(stageClusters);
                const numClusters = clusterNames?.length || 0;
                return numClusters;
              });
            })
            .flat(Infinity);

          min = Math.min(...numClustersArray) || 0;
          max = Math.max(...numClustersArray) || 0;
          stageNames = currentRTFO?.stages;
        }

        return !min || !max ? 0 : min === max ? min : `${min}-${max}`;
      }
      case 'Number of shots per cluster': {
        let min = 0;
        let max = 0;
        let stageNames = [];

        if (wellName) {
          stageNames = currentRTFO?.stages?.filter(s => s.toLowerCase()?.includes(wellName?.toLowerCase())) || [];

          const numShotsArray = stageNames
            ?.map(stageName => {
              const stageNumber = stageName?.split('_')?.[2];
              const stageClusters = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[stageNumber]?.clusters;
              const clusterNames = stageClusters && Object.keys(stageClusters);
              return clusterNames?.map(clusterName => {
                return stageClusters[clusterName]?.numShots;
              });
            })
            ?.flat(Infinity);

          min = Math.min(...numShotsArray) || 0;
          max = Math.max(...numShotsArray) || 0;
        } else {
          const numShotsArray = Object.keys(currentRTFO?.perfSchedule?.wells)
            .map(wellName => {
              stageNames = currentRTFO?.stages?.filter(s => s.toLowerCase()?.includes(wellName?.toLowerCase())) || [];

              return stageNames?.map(stageName => {
                const stageNumber = stageName?.split('_')?.[2];
                const stageClusters = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[stageNumber]?.clusters;
                const clusterNames = stageClusters && Object.keys(stageClusters);
                return clusterNames?.map(clusterName => {
                  return stageClusters[clusterName]?.numShots;
                });
              });
            })
            .flat(Infinity);

          min = Math.min(...numShotsArray) || 0;
          max = Math.max(...numShotsArray) || 0;
        }

        return !min || !max ? 0 : min === max ? min : `${min}-${max}`;
      }
      case 'Entry hole size': {
        let min = 0;
        let max = 0;
        let stageNames = [];

        if (wellName) {
          stageNames = currentRTFO?.stages?.filter(s => s.toLowerCase()?.includes(wellName?.toLowerCase())) || [];

          const entryHolesArray = stageNames
            ?.map(stageName => {
              const stageNumber = stageName?.split('_')?.[2];
              const stageClusters = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[stageNumber]?.clusters;
              const clusterNames = stageClusters && Object.keys(stageClusters);
              return clusterNames?.map(clusterName => {
                return stageClusters[clusterName]?.entryHoleDiameter;
              });
            })
            ?.flat(Infinity);

          min = Math.min(...entryHolesArray) || 0;
          max = Math.max(...entryHolesArray) || 0;
        } else {
          const entryHolesArray = Object.keys(currentRTFO?.perfSchedule?.wells)
            .map(wellName => {
              stageNames = currentRTFO?.stages?.filter(s => s.toLowerCase()?.includes(wellName?.toLowerCase())) || [];

              return stageNames?.map(stageName => {
                const stageNumber = stageName?.split('_')?.[2];
                const stageClusters = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[stageNumber]?.clusters;
                const clusterNames = stageClusters && Object.keys(stageClusters);
                return clusterNames?.map(clusterName => {
                  return stageClusters[clusterName]?.entryHoleDiameter;
                });
              });
            })
            .flat(Infinity);

          min = Math.min(...entryHolesArray) || 0;
          max = Math.max(...entryHolesArray) || 0;
        }

        return !min || !max ? 0 : min === max ? `${min} mm` : `${min}-${max} mm`;
      }
      case 'Total water used for frac': {
        if (wellName) {
          const totalVolumeArray = currentRTFO?.stages
            ?.filter(s => s.toLowerCase()?.includes(wellName?.toLowerCase()))
            ?.map(s => currentRTFO?.stageData?.[s]?.metrics?.totalCleanVolume || 0);
          return `${(totalVolumeArray?.reduce((tot, curr) => tot + curr, 0) || 0)?.toFixed(3)} m3`;
        }
        const totalVolumeArray = currentRTFO?.stages?.map(s => currentRTFO?.stageData?.[s]?.metrics?.totalCleanVolume || 0);
        return `${(totalVolumeArray?.reduce((tot, curr) => tot + curr, 0) || 0)?.toFixed(3)} m3`;
      }
      case 'Total water used for pumpdown': {
        return 'n/a';
      }
      case 'Total proppant': {
        if (wellName) {
          const totalProppantArray = currentRTFO?.stages
            ?.filter(s => s.toLowerCase()?.includes(wellName?.toLowerCase()))
            ?.map(s => currentRTFO?.stageData?.[s]?.metrics?.totalProppant || 0);
          return `${(totalProppantArray?.reduce((tot, curr) => tot + curr, 0) || 0)?.toFixed(3)} ton`;
        }
        const totalProppantArray = currentRTFO?.stages?.map(s => currentRTFO?.stageData?.[s]?.metrics?.totalProppant || 0);
        return `${(totalProppantArray?.reduce((tot, curr) => tot + curr, 0) || 0)?.toFixed(3)} ton`;
      }
      case 'Total chemical/FR': {
        if (wellName) {
          const totalChemicalArray = currentRTFO?.stages
            ?.filter(s => s.toLowerCase()?.includes(wellName?.toLowerCase()))
            ?.map(
              s =>
                (currentRTFO?.stageData?.[s]?.metrics?.frPerFluid * currentRTFO?.stageData?.[s]?.metrics?.totalCleanVolume || 0) +
                (currentRTFO?.stageData?.[s]?.metrics?.frPerFluid2 * currentRTFO?.stageData?.[s]?.metrics?.totalCleanVolume || 0)
            );
          return `${(totalChemicalArray?.reduce((tot, curr) => tot + curr, 0) || 0)?.toFixed(3)} kg`;
        }
        const totalChemicalArray = currentRTFO?.stages?.map(
          s =>
            (currentRTFO?.stageData?.[s]?.metrics?.frPerFluid * currentRTFO?.stageData?.[s]?.metrics?.totalCleanVolume || 0) +
            (currentRTFO?.stageData?.[s]?.metrics?.frPerFluid2 * currentRTFO?.stageData?.[s]?.metrics?.totalCleanVolume || 0)
        );
        return `${(totalChemicalArray?.reduce((tot, curr) => tot + curr, 0) || 0)?.toFixed(3)} kg`;
      }
      case 'Number of pumpdowns': 
      case 'Frac plugs used': {
        if (wellName) {
          return currentRTFO?.stages?.filter(s => s.toLowerCase()?.includes(wellName?.toLowerCase()))?.length - 1;
        }
        const numWells = [...new Set(currentRTFO?.stages?.map(s => s.split('_')?.[1]))]?.length || 0;
        return currentRTFO?.stages?.length - numWells;
      }
      case 'Start date': {
        if (wellName) {
          const firstStageName = currentRTFO?.stages?.find(s => s.toLowerCase()?.includes(wellName?.toLowerCase()));
          return new Date(currentRTFO?.stageData?.[firstStageName]?.stageStartTimestamp)?.toLocaleString();
        }
        return new Date(currentRTFO?.stageData?.[currentRTFO?.stages?.[0]]?.stageStartTimestamp)?.toLocaleString();
      }
      case 'End date': {
        if (wellName) {
          const lastStageName = currentRTFO?.stages?.findLast(s => s.toLowerCase()?.includes(wellName?.toLowerCase()));
          return new Date(currentRTFO?.stageData?.[lastStageName]?.stageEndTimestamp)?.toLocaleString();
        }
        return new Date(currentRTFO?.stageData?.[currentRTFO?.stages?.slice(-1)?.[0]]?.stageEndTimestamp)?.toLocaleString();
      }
      case 'Total frac time': {
        if (wellName) {
          const totalFracTimesArray = currentRTFO?.stages
            ?.filter(s => s.toLowerCase()?.includes(wellName?.toLowerCase()))
            ?.map(s => currentRTFO?.stageData?.[s]?.metrics?.stageTime || 0);
          return `${(totalFracTimesArray?.reduce((tot, curr) => tot + curr, 0) / 60 / 24 || 0)?.toFixed(1)} days`;
        }
        const totalFracTimesArray = currentRTFO?.stages?.map(s => currentRTFO?.stageData?.[s]?.metrics?.stageTime || 0);
        return `${(totalFracTimesArray?.reduce((tot, curr) => tot + curr, 0) / 60 / 24 || 0)?.toFixed(1)} days`;
      }
      case 'Total time between fracs': {
        if (wellName) {
          const startTimestamp =
            currentRTFO?.stageData?.[currentRTFO?.stages?.find(s => s.toLowerCase()?.includes(wellName?.toLowerCase()))]
              ?.stageStartTimestamp;
          const endTimestamp =
            currentRTFO?.stageData?.[currentRTFO?.stages?.findLast(s => s.toLowerCase()?.includes(wellName?.toLowerCase()))]
              ?.stageEndTimestamp;
          const totalDuration = (endTimestamp - startTimestamp) / 60000;
          const totalFracTimesArray = currentRTFO?.stages
            ?.filter(s => s.toLowerCase()?.includes(wellName?.toLowerCase()))
            ?.map(s => currentRTFO?.stageData?.[s]?.metrics?.stageTime || 0);
          const totalFracTime = totalFracTimesArray?.reduce((tot, curr) => tot + curr, 0) || 0;
          return `${((totalDuration - totalFracTime) / 60 / 24)?.toFixed(1)} days`;
        }
        const startTimestamp = currentRTFO?.stageData?.[currentRTFO?.stages?.[0]]?.stageStartTimestamp;
        const endTimestamp = currentRTFO?.stageData?.[currentRTFO?.stages?.slice(-1)?.[0]]?.stageEndTimestamp;
        const totalDuration = (endTimestamp - startTimestamp) / 60000;
        const totalFracTimesArray = currentRTFO?.stages?.map(s => currentRTFO?.stageData?.[s]?.metrics?.stageTime || 0);
        const totalFracTime = totalFracTimesArray?.reduce((tot, curr) => tot + curr, 0) || 0;
        return `${((totalDuration - totalFracTime) / 60 / 24)?.toFixed(1)} days`;
      }
      case 'Average pumpdown time': {
        return 'n/a';
      }
      case 'Average time on bottom': {
        return 'n/a';
      }
      case 'Completed length': {
        if (wellName) {
          const totalLengthsArray = currentRTFO?.stages
            ?.filter(s => s.toLowerCase()?.includes(wellName?.toLowerCase()))
            ?.map(s => currentRTFO?.stageData?.[s]?.metrics?.stageLength || 0);
          return `${(totalLengthsArray?.reduce((tot, curr) => tot + curr, 0) || 0)?.toFixed(1)} m`;
        }
        const totalLengthsArray = currentRTFO?.stages?.map(s => currentRTFO?.stageData?.[s]?.metrics?.stageLength || 0);
        return `${(totalLengthsArray?.reduce((tot, curr) => tot + curr, 0) || 0)?.toFixed(1)} m`;
      }
      case 'Casing id': {
        return `${currentRTFO?.settings?.wells?.[wellName?.toLowerCase()]?.[casingName]?.id} mm`;
      }
      case 'Casing od': {
        return `${currentRTFO?.settings?.wells?.[wellName?.toLowerCase()]?.[casingName]?.od} mm`;
      }
      case 'Casing md': {
        return `${currentRTFO?.settings?.wells?.[wellName?.toLowerCase()]?.[casingName]?.md} mMD`;
      }
      case 'Casing fc': {
        return `${currentRTFO?.settings?.wells?.[wellName?.toLowerCase()]?.[casingName]?.fc}`;
      }
      case 'Casing plExp': {
        return `${currentRTFO?.settings?.wells?.[wellName?.toLowerCase()]?.[casingName]?.plExp}`;
      }
      default:
        return 'n/a';
    }
  }
};

export default RTFOCommandCenter;
